import { memo } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { Theme } from "@mui/material";
import { fNumber } from "util/formaters";

export type ChartLegendItem = {
    readonly id: string;
    readonly color: string;
    readonly total: number;
};

type ChartLegendProps = {
    readonly items: readonly ChartLegendItem[];
    readonly formatValue?: (value: number) => string;
    readonly postfix?: string;
    readonly width?: number;
};

const defaultFormat = (value: number) => fNumber(value, ',.2f');

const ChartLegend = ({
    items,
    formatValue = defaultFormat,
    postfix,
    width = 120
}: ChartLegendProps) => {
    if (!items.length) {
        return null;
    }

    return (
        <Box
            sx={({ palette }: Theme) => ({
                position: 'absolute',
                top: 50,
                right: 0,
                width,
                fontFamily: 'var(--manrope-font)',
                color: palette.text.primary,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                fontSize: 12,
                pointerEvents: 'auto'
            })}
        >
            {items.map(({ id, color, total }) => (
                <Tooltip
                    key={id}
                    arrow
                    placement="left"
                    title={(
                        <Box
                            sx={{
                                fontFamily: 'var(--manrope-font)',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {formatValue(total)}{postfix ? ` ${postfix}` : ''}
                        </Box>
                    )}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'default',
                            opacity: 0.85,
                            '&:hover': {
                                opacity: 1
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: color,
                                flexShrink: 0,
                                border: '1px solid rgba(0, 0, 0, 0.5)'
                            }}
                        />
                        <Box
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {id}
                        </Box>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

export default memo(ChartLegend);
